import { Footer } from "@/components/Footer";
import { ChevronLeft, ShieldCheck } from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { EmailLink } from "@/components/EmailLink";

const rules = [
  {
    title: "No spam",
    body: "Don't use workflows or Smart FAQ to send spam, unsolicited bulk messages, or messages people didn't ask for.",
  },
  {
    title: "Don't pretend to be someone else",
    body: "Don't build workflows that impersonate a person or a company, or that hide who a message is really from.",
  },
  {
    title: "Follow each app's rules",
    body: "When a workflow uses Gmail, Outlook, Slack, Microsoft Teams, Notion, HubSpot or any other app, follow that app's terms and limits.",
  },
  {
    title: "Nothing unlawful or harmful",
    body: "Don't use ClearMakes for anything illegal, to harass or threaten people, to collect data you have no right to, or to break into systems or accounts.",
  },
  {
    title: "Check what AI writes",
    body: "AI output can be wrong. Check anything a workflow or Smart FAQ writes or sends in your name before you rely on it.",
  },
];

const AcceptableUse = () => (
  <>
    <HelmetProvider>
      <Helmet>
        <title>Acceptable Use Policy | ClearMakes</title>
        <meta name="description" content="The rules for using ClearMakes workflows and Smart FAQ: no spam, no impersonation, follow each app's terms, nothing unlawful, and check what AI writes." />
      </Helmet>
    </HelmetProvider>
    <div className="min-h-screen bg-[#0A0F1C] text-foreground font-sans flex flex-col relative selection:bg-primary/30">
      <div className="absolute inset-0 noise-texture opacity-20 pointer-events-none" />

      <main className="flex-grow pt-32 pb-20 container relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6 border border-primary/20">
            <ShieldCheck className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white tracking-tight">Acceptable Use Policy</h1>
          <p className="text-lg text-muted-foreground">The rules for using ClearMakes</p>
          <p className="text-sm text-gray-500 mt-2 font-mono uppercase tracking-widest">Last Updated: September 28, 2026</p>
        </div>

        <div className="space-y-10 text-gray-300 glass-panel border border-white/5 rounded-[2.5rem] p-8 md:p-16">
          <section>
            <p className="leading-relaxed text-lg text-muted-foreground">
              ClearMakes lets you build workflows that read and send messages for you, and Smart FAQ answers questions
              in your name. That makes it important to use them fairly. This policy is part of our{" "}
              <a href="/terms" className="text-primary hover:underline">Terms of Service</a>.
            </p>
          </section>

          {rules.map((r, i) => (
            <section key={r.title}>
              <h2 className="text-2xl font-semibold text-white mb-4">
                {i + 1}. {r.title}
              </h2>
              <p className="leading-relaxed text-lg text-muted-foreground">{r.body}</p>
            </section>
          ))}

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">{rules.length + 1}. If a rule is broken</h2>
            <p className="leading-relaxed text-lg text-muted-foreground">
              We may turn off a workflow, or suspend or close an account, that breaks these rules. To report misuse,
              email{" "}
              <EmailLink email="contact@clearmakes.com" className="text-primary hover:underline font-medium">
                contact@clearmakes.com
              </EmailLink>
              .
            </p>
          </section>
        </div>

        <div className="pt-16 text-center">
          <a href="/" className="text-primary hover:text-white transition-colors font-medium flex items-center justify-center gap-2">
            <ChevronLeft size={16} /> Back to Home
          </a>
        </div>
      </main>

      <Footer />
    </div>
  </>
);

export default AcceptableUse;
